import type { EventRow, SchemaStatus } from '@growth/client';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { EnvironmentSwitch } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { Card, DataTable, ErrorBox, JsonView, StatusBadge } from '../components/ui.tsx';
import { useGlobalFilters } from '../filters.ts';
import { fmtInt, fmtTime, shortId } from '../format.ts';
import { useSession } from '../session.tsx';
import { paramSummary } from './RunDetail.tsx';

const KEEP = 500;
const POLL_MS = 2000;

export function LivePage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters({ defaultEnvironment: 'development' });
  const env = filters.environment;
  const { api } = useSession();
  const [name, setName] = useState('');
  const [installation, setInstallation] = useState('');
  const [status, setStatus] = useState<SchemaStatus | ''>('');
  const [paused, setPaused] = useState(false);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [selected, setSelected] = useState<EventRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastId = useRef<number | null>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const query = { environment: env, name: name.replace(/\s+/g, '') || undefined, installation_id: installation.trim() || undefined, status: status || undefined };
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ctrl = new AbortController();
    lastId.current = null;
    setEvents([]);
    setSelected(null);

    const tick = async () => {
      if (!alive) return;
      if (!pausedRef.current && document.visibilityState === 'visible') {
        try {
          if (lastId.current === null) {
            // First load: newest 100, then tail from the highest id.
            const page = await api.events(gameId, { ...query, limit: 100 }, ctrl.signal);
            if (!alive) return;
            setEvents(page.events);
            if (page.events.length) lastId.current = Math.max(...page.events.map((e) => Number(e.id)));
          } else {
            const page = await api.events(gameId, { ...query, after_id: lastId.current, limit: 500 }, ctrl.signal);
            if (!alive) return;
            if (page.events.length) {
              lastId.current = page.last_id ?? lastId.current;
              setEvents((prev) => [...[...page.events].reverse(), ...prev].slice(0, KEEP));
            }
          }
          setError(null);
        } catch (e) {
          if (!alive || ctrl.signal.aborted) return;
          setError((e as Error).message);
        }
      }
      timer = setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      alive = false;
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
    // `query` is captured through queryKey.
  }, [api, gameId, queryKey]);

  const invalid = events.filter((e) => e.schema_status !== 'valid').length;
  const q = env === 'production' ? '' : `?environment=${env}`;

  return (
    <>
      <PageHeader
        title="Eventos en vivo"
        sub="Cada evento tal como llega a la API (actualiza cada 2 s). Desde el editor de Unity los eventos van a Desarrollo; la cola local se ve en Window → Game Growth → Debugger."
      />
      <div className="filterbar">
        <EnvironmentSwitch filters={filters} />
        <label className="field">
          <span>Eventos (separados por comas)</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="run_start,run_end" />
        </label>
        <label className="field">
          <span>Installation ID</span>
          <input className="input" value={installation} onChange={(e) => setInstallation(e.target.value)} placeholder="uuid" />
        </label>
        <label className="field">
          <span>Validación</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as SchemaStatus | '')}>
            <option value="">Todos</option>
            <option value="valid">Válidos</option>
            <option value="invalid">Inválidos</option>
            <option value="unregistered">No registrados</option>
          </select>
        </label>
        <span className="spacer" />
        <span className="muted small" aria-live="polite">
          {fmtInt(events.length)} eventos{invalid ? ` · ${fmtInt(invalid)} con problemas de esquema` : ''}
        </span>
        <button type="button" className="btn small" aria-pressed={paused} onClick={() => setPaused((p) => !p)}>
          {paused ? 'Reanudar' : 'Pausar'}
        </button>
        <button type="button" className="btn small ghost" onClick={() => (setEvents([]), setSelected(null))}>
          Limpiar
        </button>
      </div>
      {error && <ErrorBox error={error} />}
      <div className="split">
        <Card>
          {events.length ? (
            <div className="scroll-panel">
              <DataTable
                compact
                selected={selected ? events.indexOf(selected) : null}
                onRowClick={(i) => setSelected(events[i] ?? null)}
                data={{
                  columns: [{ label: 'Hora' }, { label: 'Evento' }, { label: 'Esquema' }, { label: 'Jugador' }, { label: 'Versión' }, { label: 'Parámetros' }],
                  rows: events.map((e) => [
                    <span key="t" title={`evento ${e.event_ts} · recibido ${e.received_at}`}>
                      {fmtTime(e.event_ts)}
                    </span>,
                    <span key="n">
                      {e.event_name}
                      {e.run_id && <span className="muted small"> · run {shortId(e.run_id, 4)}</span>}
                    </span>,
                    e.schema_status === 'valid' ? <span key="s" className="muted small">válido</span> : <StatusBadge key="s" status={e.schema_status === 'invalid' ? 'error' : 'warn'} />,
                    <Link key="p" className="mono" to={`/g/${gameId}/players/${e.installation_id}${q}`} onClick={(ev) => ev.stopPropagation()}>
                      {shortId(e.installation_id)}
                    </Link>,
                    `${e.app_version ?? '—'}${e.build_type && e.build_type !== 'release' ? ` (${e.build_type})` : ''}`,
                    <span key="p2" className="muted small">
                      {paramSummary(e.params, 3)}
                    </span>,
                  ]),
                }}
              />
            </div>
          ) : (
            <div className="empty">
              Esperando eventos en {env === 'development' ? 'Desarrollo' : env}… Pulsa Play en Unity con el SDK configurado, o ejecuta el simulador: <code>pnpm simulate</code>.
            </div>
          )}
        </Card>
        <div className="sticky">
          <Card title={selected ? selected.event_name : 'Detalle del evento'} subtitle={selected ? `seq ${selected.seq ?? '—'} · ${selected.sdk_version ?? ''}` : undefined}>
            {selected ? (
              <>
                {selected.schema_status !== 'valid' && (
                  <div className="notice" style={{ marginBottom: 8 }}>
                    <StatusBadge status={selected.schema_status === 'invalid' ? 'error' : 'warn'} /> {JSON.stringify(selected.schema_errors)}
                  </div>
                )}
                <JsonView value={selected} />
              </>
            ) : (
              <div className="empty">Selecciona un evento para ver su JSON.</div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
